import React, { useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Check, ShoppingCart, CornerUpLeft } from "lucide-react";
import { useI18n } from "../contexts/I18nContext";
import { findCategory, findProduct, productsByCategory } from "../data/products";
import { useCart } from "../contexts/CartContext";
import { useComposition } from "../contexts/CompositionContext";
import { toast } from "sonner";
import { captureCurrentPreview } from "../lib/captureCurrentPreview";
import ProductCard from "../components/ProductCard";

/**
 * Vérifie qu'une configuration est suffisamment complète pour être ajoutée
 * au panier. Empêche l'ajout d'items "vides" (sentinel `_empty_` pour les
 * croix, aucun texte pour les lettres) qui produiraient une fiche de
 * production sans aucun détail technique.
 */
function isConfigurationValid(slug, cfg) {
  if (!cfg || typeof cfg !== "object") return false;
  // Croix : doit avoir au moins 1 item réel (pas de sentinel _empty_)
  if (slug && (slug.startsWith("croix-") || slug.includes("croix"))) {
    const items = cfg.items || [];
    return items.some(
      (it) =>
        it && it.designId && !String(it.designId).startsWith("_empty_"),
    );
  }
  // Lettres : doit avoir au moins une ligne avec du texte non vide
  if (
    slug &&
    (slug.startsWith("lettre-") ||
      slug.startsWith("enseigne-") ||
      ["boitier", "retro", "neon", "chassis", "inox"].some((k) => slug.includes(k)))
  ) {
    const lines = cfg.lines || [];
    return lines.some((l) => l && (l.text || "").trim().length > 0);
  }
  // Caducée et autres : toujours valide (designId par défaut = "1")
  return true;
}
import NeonConfigurator from "../components/NeonConfigurator";
import BoxLetterConfigurator from "../components/BoxLetterConfigurator";
import CrossConfigurator from "../components/CrossConfigurator";
import CaduceeConfigurator from "../components/CaduceeConfigurator";
import ConfiguratorTabs, { CONFIGURATOR_TABS } from "../components/ConfiguratorTabs";
import EmailGateModal, { getStoredVisitorEmail } from "../components/EmailGateModal";

const NEON_CUSTOM_SLUG = "enseigne-interieur-neon-led";
const BOXLETTER_SLUG = "lettre-boitier-alu";
const RETRO_SLUG = "lettre-retro-eclaire";
const INOX_SLUG = "lettre-inox-3d";
const CHASSIS_SLUG = "enseigne-boitier-chassis";
const CROSS_SLUG = "croix-neon-led";
const CROSS_CLASSIQUE_SLUG = "croix-classique-led";
const CROSS_DOTMATRIX_SLUG = "croix-point-a-point";
const CADUCEE_SLUG = "caducee";
const USES_CROSS_CONFIGURATOR = (s) => s === CROSS_SLUG || s === CROSS_CLASSIQUE_SLUG || s === CROSS_DOTMATRIX_SLUG;
const USES_BOXLETTER_CONFIGURATOR = (s) => s === BOXLETTER_SLUG || s === RETRO_SLUG || s === INOX_SLUG || s === CHASSIS_SLUG || s === NEON_CUSTOM_SLUG;
const USES_CADUCEE_CONFIGURATOR = (s) => s === CADUCEE_SLUG;
const SIGN_TYPE_FOR_SLUG = (s) => s === RETRO_SLUG ? "retro" : s === INOX_SLUG ? "inox" : s === CHASSIS_SLUG ? "chassis" : s === NEON_CUSTOM_SLUG ? "neon" : "boitier";

export default function ProductDetail() {
  const { category: catSlug, slug } = useParams();
  const { t, lang } = useI18n();
  const navigate = useNavigate();
  const { addItem } = useCart();
  const { getConfigFor, setConfigFor, previousSlug, setPreviousSlug } = useComposition();

  const product = useMemo(() => findProduct(slug), [slug]);
  const category = useMemo(() => findCategory(catSlug), [catSlug]);
  // Produit précédent (pour le bandeau "Retour à …" déposé par la pill Montage)
  const previousProduct = useMemo(
    () => (previousSlug && previousSlug !== slug ? findProduct(previousSlug) : null),
    [previousSlug, slug]
  );
  const goBackToPrevious = () => {
    if (!previousProduct) return;
    const prev = previousProduct;
    setPreviousSlug(null);
    navigate(`/produits/${prev.category}/${prev.slug}`);
  };

  const [activeImage, setActiveImage] = useState(product?.image);
  // Email gate — ne s'affiche QU'après que l'utilisateur ait fait sa
  // première sélection dans le configurateur, ET seulement après une
  // temporisation de 60 secondes. Une fois affichée OU une fois l'email
  // capturé, on pose un flag "asked" pour ne PLUS JAMAIS redemander
  // (même après un Réinitialiser). Spec utilisateur stricte.
  const isConfiguratorPage = USES_CROSS_CONFIGURATOR(slug) || USES_BOXLETTER_CONFIGURATOR(slug) || USES_CADUCEE_CONFIGURATOR(slug);
  const hasEmail = !!getStoredVisitorEmail();
  const hasAlreadyAsked = typeof window !== "undefined"
    && localStorage.getItem("signapharma-email-gate-asked-v1") === "1";
  const [showEmailGate, setShowEmailGate] = useState(false);

  // Détecte la PREMIÈRE interaction utilisateur sur la page (click ou tap),
  // puis arme un timer de 60s avant d'afficher la modale.
  React.useEffect(() => {
    if (!isConfiguratorPage) return undefined;
    if (hasEmail || hasAlreadyAsked) return undefined;
    let timeoutId = null;
    let armed = false;
    const onFirstInteraction = () => {
      if (armed) return;
      armed = true;
      window.removeEventListener("click", onFirstInteraction, true);
      window.removeEventListener("touchstart", onFirstInteraction, true);
      timeoutId = setTimeout(() => {
        // Re-check avant d'afficher (l'utilisateur a pu naviguer ailleurs).
        if (getStoredVisitorEmail()) return;
        if (localStorage.getItem("signapharma-email-gate-asked-v1") === "1") return;
        try { localStorage.setItem("signapharma-email-gate-asked-v1", "1"); } catch (_e) {}
        setShowEmailGate(true);
      }, 60 * 1000); // 60 secondes
    };
    window.addEventListener("click", onFirstInteraction, true);
    window.addEventListener("touchstart", onFirstInteraction, true);
    return () => {
      window.removeEventListener("click", onFirstInteraction, true);
      window.removeEventListener("touchstart", onFirstInteraction, true);
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [isConfiguratorPage, hasEmail, hasAlreadyAsked]);
  // Configuration persistée globalement (CompositionContext) — partage la
  // scène (façade + jour/nuit) entre tous les configurateurs et conserve
  // chaque montage par slug produit dans le localStorage.
  const config = useMemo(() => (slug ? getConfigFor(slug) : {}), [slug, getConfigFor]);
  const setConfig = (next) => slug && setConfigFor(slug, next);

  if (!product) {
    return (
      <div className="max-w-[1400px] mx-auto px-6 py-32 text-center">
        <h1 className="font-display text-3xl">Product not found</h1>
        <Link to="/produits" className="btn-ghost-neon mt-6 inline-flex">
          {t.common.back}
        </Link>
      </div>
    );
  }

  const name = product.name[lang] || product.name.fr;
  const desc = product.description[lang] || product.description.fr;
  const features = product.features[lang] || product.features.fr;
  const gallery = product.gallery?.length ? product.gallery : [product.image];
  const related = productsByCategory(product.category)
    .filter((p) => p.slug !== product.slug)
    .slice(0, 3);

  const handleAddToCart = async () => {
    // 1) Force les configurateurs à pousser leur état COURANT vers le
    // CompositionContext même si l'utilisateur n'a pas touché à la preview
    // (cas : clic direct "Ajouter au panier" sur les valeurs par défaut).
    // Le handler écrit aussi la config dans window._signapharmaConfigCapture
    // pour lecture synchrone (le setState du contexte est asynchrone).
    window.dispatchEvent(new CustomEvent("signapharma:capture-config"));
    const capturedNow =
      (typeof window !== "undefined" &&
        window._signapharmaConfigCapture &&
        window._signapharmaConfigCapture[slug]) || null;
    const freshConfig = capturedNow || (slug ? getConfigFor(slug) : config);

    // Validation : refuse l'ajout si la configuration est vide
    // (sentinel _empty_ pour croix / pas de texte pour lettres)
    if (!isConfigurationValid(product.slug, freshConfig)) {
      toast.error(
        lang === "fr"
          ? "Veuillez d'abord sélectionner un modèle / saisir votre texte avant d'ajouter au panier."
          : lang === "de"
          ? "Bitte zuerst ein Modell auswählen / Text eingeben."
          : lang === "it"
          ? "Seleziona prima un modello / inserisci il testo."
          : "Please select a model / enter your text first.",
      );
      return;
    }

    // 2) Capture l'aperçu actuel du configurateur (Cross, Letters, Caducée, Neon)
    // pour l'embarquer dans la fiche de production PDF.
    const previewImage = await captureCurrentPreview({ slug: product.slug });
    addItem({
      product_slug: product.slug,
      product_name: product.name,
      image: product.image,
      configuration: previewImage
        ? { ...freshConfig, previewImage }
        : freshConfig,
    });
    toast.success(
      lang === "fr"
        ? "Ajouté au panier"
        : lang === "de"
        ? "Hinzugefügt"
        : lang === "it"
        ? "Aggiunto al carrello"
        : "Added to cart"
    );
  };

  const handleQuote = async () => {
    window.dispatchEvent(new CustomEvent("signapharma:capture-config"));
    const capturedNow =
      (typeof window !== "undefined" &&
        window._signapharmaConfigCapture &&
        window._signapharmaConfigCapture[slug]) || null;
    const freshConfig = capturedNow || (slug ? getConfigFor(slug) : config);
    if (!isConfigurationValid(product.slug, freshConfig)) {
      toast.error(
        lang === "fr"
          ? "Veuillez d'abord sélectionner un modèle / saisir votre texte."
          : "Please select a model / enter your text first.",
      );
      return;
    }
    const previewImage = await captureCurrentPreview({ slug: product.slug });
    addItem({
      product_slug: product.slug,
      product_name: product.name,
      image: product.image,
      configuration: previewImage
        ? { ...freshConfig, previewImage }
        : freshConfig,
    });
    navigate("/checkout");
  };
  // eslint-disable-next-line no-unused-vars
  const _keepHandleQuote = handleQuote;

  return (
    <div data-testid="product-detail-page">
      {showEmailGate && (
        <EmailGateModal
          projectContext={product?.name?.[lang] || product?.name?.fr || product?.slug}
          onClose={() => setShowEmailGate(false)}
        />
      )}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-10">
        <nav className="font-mono text-xs uppercase tracking-widest text-zinc-500 mb-8">
          <Link to="/" className="hover:text-[#39FF14]">
            {t.nav.home}
          </Link>
          <span className="mx-2">/</span>
          <Link to="/produits" className="hover:text-[#39FF14]">
            {t.nav.products}
          </Link>
          {category && (
            <>
              <span className="mx-2">/</span>
              <Link
                to={`/produits/${category.slug}`}
                className="hover:text-[#39FF14]"
              >
                {t.cat[category.key].name}
              </Link>
            </>
          )}
          <span className="mx-2">/</span>
          <span className="text-[#39FF14]">{name}</span>
        </nav>

        {/* Bandeau "Retour à mon montage X" — déposé par la pill Mon Montage
            quand l'utilisateur a navigué depuis une autre page produit pour
            consulter / éditer un élément. Un clic le ramène à l'étape précédente
            de son montage (les configurations sont déjà persistées). */}
        {previousProduct && (
          <div
            data-testid="return-to-previous-banner"
            className="mb-6 flex items-center justify-between gap-4 px-4 py-3 border border-[#39FF14]/40 bg-[#39FF14]/[0.06]"
          >
            <span className="font-mono text-[11px] uppercase tracking-widest text-zinc-200">
              {lang === "fr" ? "Vous travailliez sur" : "You were working on"} :{" "}
              <span className="text-[#39FF14]">
                {previousProduct.name[lang] || previousProduct.name.fr}
              </span>
            </span>
            <button
              type="button"
              onClick={goBackToPrevious}
              data-testid="return-to-previous-btn"
              className="inline-flex items-center gap-2 px-3 py-1.5 border border-[#39FF14] bg-[#39FF14]/15 text-[#39FF14] hover:bg-[#39FF14]/25 transition-colors font-mono text-[10px] uppercase tracking-widest"
            >
              <CornerUpLeft size={12} />
              {lang === "fr" ? "Retour à mon montage" : "Back to my composition"}
            </button>
          </div>
        )}

        <div className={USES_BOXLETTER_CONFIGURATOR(product.slug) || USES_CROSS_CONFIGURATOR(product.slug) || USES_CADUCEE_CONFIGURATOR(product.slug) || product.slug === NEON_CUSTOM_SLUG ? "block" : "grid lg:grid-cols-2 gap-12 xl:gap-20"}>
          {/* Gallery — caché sur les pages avec configurateur visuel pleine largeur */}
          {!USES_BOXLETTER_CONFIGURATOR(product.slug) && !USES_CROSS_CONFIGURATOR(product.slug) && !USES_CADUCEE_CONFIGURATOR(product.slug) && product.slug !== NEON_CUSTOM_SLUG && (
            <div className="space-y-4">
              <div className="aspect-square bg-[#080808] border border-white/5 overflow-hidden relative">
                <img
                  src={activeImage}
                  alt={name}
                  className="w-full h-full object-cover"
                  data-testid="pdp-main-image"
                />
                <div className="absolute inset-0 pointer-events-none"
                     style={{ background: "radial-gradient(ellipse at center, rgba(57,255,20,0.08), transparent 65%)" }} />
              </div>
              {gallery.length > 1 && (
                <div className="grid grid-cols-4 gap-3">
                  {gallery.map((img, i) => (
                    <button
                      key={i}
                      onClick={() => setActiveImage(img)}
                      data-testid={`pdp-thumb-${i}`}
                      className={`aspect-square bg-[#080808] border transition-all ${
                        activeImage === img ? "border-[#39FF14]" : "border-white/5 hover:border-white/20"
                      }`}
                    >
                      <img src={img} alt="" className="w-full h-full object-cover" />
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Info & configurator */}
          <div>
            {product.slug !== CROSS_SLUG && product.slug !== CROSS_CLASSIQUE_SLUG && product.slug !== NEON_CUSTOM_SLUG && (
              <>
                <div className="label-tech mb-3">
                  {category && t.cat[category.key].name}
                </div>
                <h1 className="font-display text-4xl sm:text-5xl font-bold uppercase tracking-tight leading-[1.05]">
                  <span className="chrome-text">{name}</span>
                </h1>
                <p className="mt-6 text-zinc-300 font-body leading-relaxed">{desc}</p>
              </>
            )}

            {/* Configurator */}
            <div className="mt-10 space-y-8">
              <div className="label-tech text-[#39FF14]">{t.pdp.configure}</div>

              {/* Onglets de navigation entre configurateurs — visibles
                  uniquement sur les pages qui utilisent un configurateur, pour
                  permettre de basculer rapidement d'un produit à l'autre tout
                  en conservant la composition globale (Phase 1). */}
              {(product.slug === NEON_CUSTOM_SLUG ||
                USES_CROSS_CONFIGURATOR(product.slug) ||
                USES_BOXLETTER_CONFIGURATOR(product.slug) ||
                USES_CADUCEE_CONFIGURATOR(product.slug)) &&
                CONFIGURATOR_TABS.some((t) => t.slug === product.slug) && (
                  <ConfiguratorTabs currentSlug={product.slug} />
                )}

              {USES_CROSS_CONFIGURATOR(product.slug) ? (
                <CrossConfigurator key={product.slug} value={config} onChange={setConfig} productSlug={product.slug} initialFamily={product.slug === CROSS_CLASSIQUE_SLUG ? "classic" : product.slug === CROSS_DOTMATRIX_SLUG ? "dotmatrix" : undefined} />
              ) : USES_BOXLETTER_CONFIGURATOR(product.slug) ? (
                <BoxLetterConfigurator key={product.slug} value={config} onChange={setConfig} productSlug={product.slug} signType={SIGN_TYPE_FOR_SLUG(product.slug)} />
              ) : USES_CADUCEE_CONFIGURATOR(product.slug) ? (
                <CaduceeConfigurator key={product.slug} value={config} onChange={setConfig} />
              ) : (
                Object.entries(product.options || {}).map(([key, values]) => (
                  <div key={key} data-testid={`option-group-${key}`}>
                    <div className="font-display uppercase tracking-widest text-xs text-zinc-400 mb-3">
                      {t.pdp[key] || key}
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {values.map((v) => {
                        const selected = config[key] === v;
                        return (
                          <button
                            key={v}
                            onClick={() => setConfig((c) => ({ ...c, [key]: v }))}
                            data-testid={`option-${key}-${v.replace(/\s+/g, "-")}`}
                            className={`px-4 py-2.5 font-mono text-xs uppercase tracking-wider border transition-all ${
                              selected
                                ? "border-[#39FF14] text-[#39FF14] bg-[#39FF14]/10 neon-glow-sm"
                                : "border-white/10 text-zinc-400 hover:border-white/30 hover:text-white"
                            }`}
                          >
                            {selected && <Check size={12} className="inline mr-1.5 -mt-0.5" />}
                            {v}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))
              )}
            </div>

            {/* Price & actions — masqué pour le Caducée car son configurateur
                a déjà son propre panneau prix + bouton "Ajouter au panier". */}
            {!USES_CADUCEE_CONFIGURATOR(product.slug) && (
            <div className="mt-10 p-6 bg-[#080808] border border-white/5">
              <div className="flex flex-col sm:flex-row gap-3">
                <button
                  onClick={handleAddToCart}
                  className="btn-neon flex-1 justify-center"
                  data-testid="pdp-add-to-cart"
                >
                  <ShoppingCart size={16} />
                  {t.pdp.add_to_cart}
                </button>
                <Link
                  to="/panier"
                  className="btn-ghost-neon flex-1 justify-center"
                  data-testid="pdp-view-cart"
                >
                  <ShoppingCart size={16} />
                  {lang === "fr" ? "Voir le panier" : "View cart"}
                </Link>
              </div>

              <p className="mt-4 text-xs text-zinc-500 font-mono uppercase tracking-widest">
                {t.pdp.delivery}
              </p>
            </div>
            )}

            {/* Features */}
            <div className="mt-10">
              <h3 className="font-display uppercase tracking-widest text-sm text-zinc-400 mb-4">
                {t.pdp.features}
              </h3>
              <ul className="space-y-3">
                {features.map((f, i) => (
                  <li key={i} className="flex items-start gap-3 text-sm text-zinc-300 font-body">
                    <span className="mt-1.5 w-1.5 h-1.5 bg-[#39FF14] neon-glow-sm shrink-0" />
                    {f}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Related */}
      {related.length > 0 && (
        <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20 border-t border-white/5 mt-10">
          <h2 className="font-display text-3xl sm:text-4xl font-bold uppercase tracking-tight mb-8">
            {t.pdp.related}
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {related.map((p, i) => (
              <ProductCard key={p.slug} product={p} index={i} />
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
